import React from "react";
import { shallowEqual } from "react-redux";
import StatusModalEntry, {
    type StatusEntryType,
} from "../../../../../common-deprecated/components/modals/status/StatusModalEntry";
import useSaveTyCode from "../../../hooks/useSaveTyCode";
import { ccSavePayloadSelector } from "../../../utils/tyCodeUtils";
import { formatObjectToJson, objectToQueryString } from "../../../../../common-deprecated/utils";
import { getAemFlexibilityMatrixConfig } from "../../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { AemFmComponent } from "../../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import {
    getUscCashPriceFromVehicle,
    showUscPrice,
    uscPriceIsExclVat,
} from "../../../../shared-logic/utils/uscPriceUtils";
import { getVehiclePriceSource } from "../../../utils/priceUtils";
import type { ModalViewType } from "../../../../../common-deprecated/types/CommonTypes";
import { addMockDeliveryConfig, CarDeliveryDestination } from "../../../utils/carDeliveryUtils";
import { doesDealerOfferCarDelivery, getTargetChannel } from "../../../../shared-logic/utils/uscUtils";
import { type UscDispatchType, useUscSelector } from "../../../redux/store";
import { enableBasedOnOperation, UscCtaTypeEnum } from "../../../utils/ctaUtils";
import { DealerOperation } from "../../../../../common-deprecated/utils/dealerConstants";
import { ExternalPriceKey } from "../../../utils/externalPriceUtils";
import { Imgload } from "../../../../shared-components/toyota/styles/utils/Skeleton.styles";
import { useUscOptionalLabel } from "../../../utils/labels";
import type { UscDebugModalType } from "../../../utils/modalConstants";
import { getUscAccessoryPackParams } from "../../../utils/accessoryUtils";
import { PdpSection } from "../../../utils/constants";
import { useDispatch } from "../../../../../shared-logic/redux/store";
import Preloader from "../../../../shared-components/toyota/styles/molecules/Preloader";
import ModalBaseView from "../../../../shared-components/toyota/styles/organisms/Modal";
import Icon from "../../../../shared-components/toyota/styles/atoms/Icon";
import Text from "../../../../shared-components/toyota/styles/atoms/Text";
import * as ModalStyles from "../../../../shared-components/toyota/styles/organisms/Modal.styles";
import * as Styles from "./UscDebugModal.styles";

const UscDebugModal = (props: ModalViewType<UscDebugModalType>): JSX.Element => {
    const { show, close, modalSettings } = props;
    // Dirty but we basically need all data anyway, and shallowEqual achieves the same as manually exporting each reducer on a separate line
    // (Do not repeat this in components that are end-user facing as this can ruin rerender performance)
    const {
        externalPrice,
        finance,
        uscSettings,
        commonSettings,
        vehicle,
        insurance,
        save,
        accessories,
        carDelivery,
        dealer,
        basket,
    } = useUscSelector((state) => state, shallowEqual);
    const ccSavePayload = useUscSelector(ccSavePayloadSelector, shallowEqual);
    const uscV2MonthlyPriceFormatLabel = useUscOptionalLabel("uscV2MonthlyPriceFormat");
    const aemFmConfig = getAemFlexibilityMatrixConfig(commonSettings, AemFmComponent.Pdp, vehicle.type)?.config;
    const aemFmFinanceConfig = aemFmConfig?.finance;
    const aemFmInsuranceConfig = aemFmConfig?.insurance;
    const dispatch = useDispatch<UscDispatchType>();

    // TODO Workaround to use the hook, ideally this becomes an action we can dispatch.
    const saveCode = useSaveTyCode((tyCode) => {
        console.log(tyCode);
    });

    const accessoryParams = vehicle.result ? getUscAccessoryPackParams(vehicle.result, uscSettings.uscEnv) : null;
    const targetChannel = getTargetChannel(commonSettings.brand, vehicle.type);

    const showPriceLabel = "Pricing source of vehicle is not priceOnRequest";

    const hasReservationFee = uscSettings.externalPriceConfig.some(
        ({ key }) => key === ExternalPriceKey.ReservationFee,
    );
    const hasDealerReservationOperation = enableBasedOnOperation(
        DealerOperation.UsedCarOnlineReservationPanE,
        dealer.dealer,
        uscSettings,
    );
    const hasDealerFinancingOperation = enableBasedOnOperation(
        DealerOperation.UsedCarOnlineFinancingPanE,
        dealer.dealer,
        uscSettings,
    );

    const statusEntries: StatusEntryType[] = [
        // TMEF
        {
            title: "TMEF",
            description: "Taxes and/or fees are shown when all flags below are true.",
            values: [
                { label: "enableExternalPrice", value: uscSettings.enableExternalPrice.toString() },
                { label: "showExternalPrice", value: externalPrice.show.toString() },
            ],
            value: "Payload",
            collapsibleContent: externalPrice.latestPayload ? formatObjectToJson(externalPrice.latestPayload) : "n/a",
            noBottomLineSpace: true,
        },
        {
            value: "Response",
            collapsibleContent: externalPrice.data ? formatObjectToJson(externalPrice.data) : "No data",
        },
        // Packs + Accessories
        {
            title: "Packs + Accessories",
            description:
                "The Accessories and the packs will be shown when the enableAccessories flag is true and 'accessories' is inside pdpSections",
            values: [
                { label: "enableAccessories", value: uscSettings.enableAccessories.toString() },
                {
                    label: "'accessories' configured in pdpSections",
                    value: uscSettings.pdpSections.includes(PdpSection.Accessories).toString(),
                },
                { label: "disableAccessoryPrice", value: uscSettings.disableAccessoryPrice.toString() },
                { label: "disableAccessoryMonthlyPrice", value: uscSettings.disableAccessoryMonthlyPrice.toString() },
                { label: "localModelCode", value: accessoryParams?.localModelCode || "n/a" },
                { label: "localConfigurationId", value: accessoryParams?.localConfigurationId || "n/a" },
                {
                    label: "Results shortCodes",
                    value:
                        [...accessories.accessoryResults, ...accessories.packResults]
                            .map((accessory) => accessory.shortCode)
                            .join() || "n/a",
                },
                {
                    label: "PADOC ES environment",
                    value: uscSettings.uscEnv, // This can be obvious from an OR perspective but this is useful info for backend teams.
                },
            ],
            value: "PADOC ES Query (accessory/pack_application-padoc)",
            collapsibleContent: accessories.esQuery ? formatObjectToJson(accessories.esQuery) : "n/a",
        },
        // Car Delivery
        {
            title: "Car Delivery",
            description: "Car delivery will be enabled when the flags below are true.",
            values: [
                { label: "enableCarDelivery", value: uscSettings.enableCarDelivery.toString() },
                dealer.initialized
                    ? {
                          label: "Dealer offers car delivery",
                          value: doesDealerOfferCarDelivery(dealer.dealer).toString(),
                      }
                    : null,
                uscSettings.enableCarDelivery
                    ? {
                          label: "Add mock config (address)",
                          value: "Click here",
                          onClick: () => {
                              // eslint-disable-next-line no-alert
                              const mockPrice = prompt("delivery price");
                              addMockDeliveryConfig(
                                  dispatch,
                                  CarDeliveryDestination.Home,
                                  Number(mockPrice),
                                  "dealerId",
                              );
                              close();
                          },
                      }
                    : null,
                uscSettings.enableCarDelivery
                    ? {
                          label: "Add mock config (dealer)",
                          value: "Click here",
                          onClick: () => {
                              // eslint-disable-next-line no-alert
                              const mockPrice = prompt("Price");
                              addMockDeliveryConfig(
                                  dispatch,
                                  CarDeliveryDestination.Dealer,
                                  Number(mockPrice),
                                  vehicle.result?.dealerId || "dealerId",
                              );
                              close();
                          },
                      }
                    : null,
            ].filter(Boolean),
            value: "Current delivery config",
            collapsibleContent: carDelivery.initialized ? formatObjectToJson(carDelivery.deliveryConfig) : "n/a",
        },
        // Finance
        {
            title: "Finance",
            description: "Monthly rates are shown when all flags below are true.",
            values: [
                {
                    label: "monthlyPaymentUrl configured in i18n",
                    value: !!uscSettings.monthlyPaymentUrl,
                },
                {
                    label: "flexibilityMatrix available",
                    value: (!!aemFmConfig).toString(),
                },
                { label: "enableFinance", value: uscSettings.enableFinance.toString() },
                {
                    label: "finance enabled in flexibilityMatrix",
                    value: (!!aemFmFinanceConfig?.enabled).toString(),
                },
                {
                    label: "vehicle has default monthly rate",
                    value: (!!vehicle.result?.financingOffers?.length && !!vehicle.result?.financeInfo).toString(),
                },
                {
                    label: showPriceLabel,
                    value: showUscPrice(getVehiclePriceSource(vehicle.result)),
                },
                {
                    label: "Vehicle price is not excl VAT",
                    value: !!vehicle.result && !uscPriceIsExclVat(getUscCashPriceFromVehicle(vehicle.result)),
                },
                {
                    label: "data source",
                    value: finance.latestResponse?.source || "n/a",
                },
                {
                    label: "Payload",
                    collapsibleContent: finance.latestPayload
                        ? formatObjectToJson(finance.latestPayload)
                        : "n/a (possibly default value used)",
                },
                {
                    label: "Response",
                    collapsibleContent: finance.latestResponse
                        ? formatObjectToJson(finance.latestResponse.data)
                        : "n/a",
                },
                {
                    label: "Finance formatting by i18n overwrite - enabled (deprecated)",
                    collapsibleContent: uscSettings.useV2MonthlyPriceFormatOverride.toString(),
                },
                {
                    label: "Finance formatting by i18n overwrite - label (deprecated)",
                    collapsibleContent: uscV2MonthlyPriceFormatLabel
                        ? formatObjectToJson(uscV2MonthlyPriceFormatLabel)
                        : "n/a",
                },
                {
                    label: "Parsed AEM Flexibility Matrix Finance Config",
                    collapsibleContent: aemFmFinanceConfig ? formatObjectToJson(aemFmFinanceConfig) : "n/a",
                },
                {
                    label: "financeModuleUrl (finance calculator)",
                    value:
                        uscSettings.financeModuleUrl ||
                        "not configured in i18n pdpused/settings, finance calculator will not be shown",
                },
                {
                    label: "monthlyPaymentUrl (monthly payments)",
                    value:
                        uscSettings.monthlyPaymentUrl ||
                        "not configured in i18n pdpused/settings, monthly payments will not be able to load",
                },
            ],
        },
        // Insurance
        {
            title: "Insurance",
            description: "Insurance rates are shown when all flags below are true.",
            values: [
                { label: "enableInsurance", value: uscSettings.enableInsurance.toString() },
                {
                    label: "Vehicle has default insurance rate",
                    value: (!!vehicle.result?.insuranceQuote).toString(),
                },
                {
                    label: "Default insurance rate is required",
                    value: uscSettings.requireDefaultInsurance.toString(),
                },
                {
                    label: showPriceLabel,
                    value: showUscPrice(getVehiclePriceSource(vehicle.result)),
                },
                {
                    label: "Vehicle price is not excl VAT",
                    value: !!vehicle.result && !uscPriceIsExclVat(getUscCashPriceFromVehicle(vehicle.result)),
                },
                {
                    label: "Flexibility Matrix available",
                    value: (!!aemFmConfig).toString(),
                },
                {
                    label: "Insurance enabled in flexibilityMatrix",
                    value: (!!aemFmInsuranceConfig?.enabled).toString(),
                },
                {
                    label: "Insurance price format (monthly/annual)",
                    value: uscSettings.insuranceIsYearly ? "annual" : "monthly",
                },
                {
                    label: "Parsed AEM Flexibility Matrix Insurance Config",
                    collapsibleContent: aemFmInsuranceConfig ? formatObjectToJson(aemFmInsuranceConfig) : "n/a",
                },
                {
                    label: "Payload",
                    collapsibleContent: insurance.latestPayload
                        ? formatObjectToJson(insurance.latestPayload)
                        : "n/a (possibly based on quoteId or read from session)",
                },
                {
                    label: "Response",
                    collapsibleContent: insurance.quote ? formatObjectToJson(insurance.quote) : "n/a",
                },
            ],
        },

        // Vehicle data
        {
            title: "Vehicle",
            values: [
                {
                    label: "ID",
                    value: vehicle.result?.id || "",
                },
                {
                    label: "Target Channel",
                    value: targetChannel,
                },
                {
                    label: "Load data:",
                    link: true,
                    value: `?${objectToQueryString({
                        ...commonSettings.query,
                        loadVehicleSourceData: true,
                    })}`,
                },
            ],
            value: "Payload",
            collapsibleContent: vehicle.source ? formatObjectToJson(vehicle.source) : "not loaded, click link above",
        },
        // Online reservation
        {
            title: "Online reservation",
            description:
                "Online reservation is shown when all flags below are true, with the exception of the (optional) reservation fee.",
            values: [
                {
                    label: "enableReserve",
                    value: uscSettings.enableReserve,
                },
                {
                    label: "Reserve key configured in the ctas setting in i18n",
                    value: uscSettings.ctas.includes(UscCtaTypeEnum.Reserve) || "Reserve CTA will not be shown",
                },
                uscSettings.enableDynamicCtas
                    ? {
                          label: "Dealer supports reserve",
                          value: hasDealerReservationOperation
                              ? true
                              : "Dealer doesn't support reservation, please check 'Toshiko dealer' block for more information.",
                      }
                    : null,
                {
                    label: "Reservation fee configured in AEM - optional",
                    value: hasReservationFee,
                },
            ],
            value: "Parsed reservation fee",
            collapsibleContent: hasReservationFee
                ? formatObjectToJson(
                      uscSettings.externalPriceConfig.find(({ key }) => key === ExternalPriceKey.ReservationFee),
                  )
                : "n/a",
        },
        // Online retailing
        {
            title: "Online retailing",
            description: "Online retailing is shown when all flags below are true.",
            values: [
                {
                    label: "enableBuy",
                    value: uscSettings.enableBuy,
                },
                {
                    label: "Buy key configured in the ctas setting in i18n",
                    value: uscSettings.ctas.includes(UscCtaTypeEnum.Buy) || "Buy CTA will not be shown",
                },
                uscSettings.enableDynamicCtas
                    ? {
                          label: "Dealer supports buy",
                          value: hasDealerFinancingOperation
                              ? true
                              : "Dealer doesn't support finance, please check 'Toshiko dealer' block for more information.",
                      }
                    : null,
            ],
        },
        // Toshiko dealer
        {
            title: "Toshiko dealer",
            description: "If the enableDynamicCtas setting is enabled, a dealer is considered a Toshiko dealer.",
            values: [
                {
                    label: "enableDynamicCtas",
                    value: uscSettings.enableDynamicCtas,
                },
                {
                    label: "Reservation operation",
                    value: hasDealerReservationOperation,
                },
                {
                    label: "Financing operation",
                    value: hasDealerFinancingOperation,
                },
                {
                    label: "Car basket finance option",
                    value: basket.activeFinanceOption,
                },
                {
                    label: "Hide buy CTA when finance option is cash",
                    value: uscSettings.dynamicCtasHideBuyCtaOnCash,
                },
                {
                    label: "Hide reserve CTA when finance option is monthly",
                    value: uscSettings.dynamicCtasHideReserveCtaOnMonthly,
                },
            ],
            value: "Parsed dealer operations",
            collapsibleContent: dealer.dealer ? formatObjectToJson(dealer.dealer.operations) : "n/a",
        },
        // CCSave
        {
            title: "CCSave",
            values: [
                { label: "toyota code (personalized)", value: save.data?.code ? String(save.data.code) : "n/a" },
                {
                    label: "Saved config (full) link",
                    value: save.data?.code ? `${uscSettings.loadSaveGatewayUrl}/config/${save.data?.code}/full` : "n/a",
                },
            ],
            value: "Payload",
            collapsibleContent: formatObjectToJson(ccSavePayload),
            noBottomLineSpace: true,
        },
        {
            value: "Parsed config",
            collapsibleContent: save.data ? formatObjectToJson(save.data) : "n/a",
            noBottomLineSpace: true,
        },
    ].filter(Boolean);

    return (
        <ModalBaseView modalIdType={modalSettings.type} onClose={close} show={show} size="medium">
            <ModalStyles.Header noBorder>
                <Text tag="h2" variant="h4">
                    Usc Debug
                </Text>
                <ModalStyles.Button type="button" onClick={close}>
                    <Icon variant="close" />
                </ModalStyles.Button>
            </ModalStyles.Header>

            <ModalStyles.Body>
                {statusEntries.map((entry, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <React.Fragment key={index}>
                        <StatusModalEntry entry={entry} />
                    </React.Fragment>
                ))}
                <Styles.Row>
                    <Styles.Col>
                        <a href="#" onClick={saveCode.wrappedCallback}>
                            Trigger save manually
                            {saveCode.loading && save.saving && (
                                <Imgload width={20} height={20}>
                                    <Preloader size={20} />
                                </Imgload>
                            )}
                        </a>
                    </Styles.Col>
                </Styles.Row>
            </ModalStyles.Body>
        </ModalBaseView>
    );
};

export default UscDebugModal;
